import { createResumeCancellationRegistry, type ResumeCancellationAttempt, type ResumeCancellationRegistry } from "./resumeSession";

/**
 * The file coordinates the in-flight map compares before letting a second caller share a running
 * upload. It is FAMILY-AWARE on purpose: the map is keyed by `uploadId`, which is the registry id
 * for the `/uploads` family, so the legacy shape's `s3Key`/`bucket` have no `/uploads` counterpart.
 * Reporting them as `""` (as this type's single non-discriminated predecessor forced every
 * `/uploads` producer to do) left two of three coordinates constant across every `/uploads` row and
 * silently degraded the guard to file size alone.
 *
 * `/uploads` therefore carries `fileName` as its per-file coordinate — `registryId` alone cannot
 * discriminate, since it IS the map key. This is defence-in-depth rather than a live data-loss fix:
 * two distinct files collide here only if they share a server-issued `registryId`, and the server
 * does not issue one twice.
 *
 * Never persisted — the map lives for the lifetime of one driver instance.
 */
export type UploadSingleFlightIdentity =
	| { kind: "legacy"; s3Key: string; bucket: string; fileSize: number }
	| { kind: "uploads"; registryId: string; fileName: string; fileSize: number };

/**
 * Two absent identities match (the pre-family behaviour for callers that supply none); one absent
 * and one present never do, and neither do two different families.
 */
function isSameUploadSingleFlightIdentity(
	existing: UploadSingleFlightIdentity | undefined,
	incoming: UploadSingleFlightIdentity | undefined
): boolean {
	if (existing === undefined || incoming === undefined) return existing === incoming;
	if (existing.kind !== incoming.kind) return false;
	if (existing.kind === "uploads" && incoming.kind === "uploads") {
		return (
			existing.registryId === incoming.registryId &&
			existing.fileName === incoming.fileName &&
			existing.fileSize === incoming.fileSize
		);
	}
	if (existing.kind === "legacy" && incoming.kind === "legacy") {
		return existing.s3Key === incoming.s3Key && existing.bucket === incoming.bucket && existing.fileSize === incoming.fileSize;
	}
	return false;
}

/**
 * Identity of one single-flight attempt. A replacement gets a new generation;
 * the old attempt remains cancellable, but its cleanup must not touch the
 * replacement's durable session.
 */
export interface UploadSingleFlightAttempt {
	readonly generation: number;
	readonly isSuperseded: () => boolean;
}

export interface ChunkedUploadDriverControlFlow {
	readonly resumeCancellationRegistry: ResumeCancellationRegistry;
	runUploadOnceByUploadId<Result>(
		uploadId: string,
		cancelSignal: AbortSignal | undefined,
		uploadOperation: (attempt: UploadSingleFlightAttempt) => Promise<Result>,
		identity?: UploadSingleFlightIdentity
	): Promise<Result>;
	runResumeWithCancellation<Result>(
		sessionId: string,
		externalSignal: AbortSignal | undefined,
		produceOutcome: (attempt: ResumeCancellationAttempt) => Promise<Result>
	): Promise<Result>;
	runPreCompletion<Result>(
		cancelSignal: AbortSignal | undefined,
		createError: () => Error,
		complete: () => Promise<Result>
	): Promise<Result>;
}

export function createChunkedUploadDriverControlFlow(): ChunkedUploadDriverControlFlow {
	const inFlightUploadsByUploadId = new Map<
		string,
		{
			promise: Promise<unknown>;
			cancelSignal?: AbortSignal;
			cancelled: boolean;
			identity?: UploadSingleFlightIdentity;
			generation: number;
		}
	>();
	let nextGeneration = 0;
	const resumeCancellationRegistry = createResumeCancellationRegistry();

	return {
		resumeCancellationRegistry,
		runUploadOnceByUploadId<Result>(
			uploadId: string,
			cancelSignal: AbortSignal | undefined,
			uploadOperation: (attempt: UploadSingleFlightAttempt) => Promise<Result>,
			identity?: UploadSingleFlightIdentity
		): Promise<Result> {
			const existingUpload = inFlightUploadsByUploadId.get(uploadId);
			if (existingUpload && !existingUpload.cancelled && !existingUpload.cancelSignal?.aborted) {
				if (!isSameUploadSingleFlightIdentity(existingUpload.identity, identity)) {
					return Promise.reject(
						new Error(`Refusing to share in-flight multipart upload ${uploadId} with a different file identity`)
					);
				}
				return existingUpload.promise as Promise<Result>;
			}
			// The old operation is still allowed to unwind and report cancellation, but its
			// generation is no longer current once the replacement is installed. Capture the
			// successor generation before scheduling it so either abort ordering is safe.
			const generation = ++nextGeneration;
			const attempt: UploadSingleFlightAttempt = {
				generation,
				isSuperseded: () => inFlightUploadsByUploadId.get(uploadId)?.generation !== generation
			};

			const uploadEntry = {
				promise: undefined as unknown as Promise<unknown>,
				cancelSignal,
				cancelled: cancelSignal?.aborted === true,
				identity,
				generation
			};
			// Observe cancellation at the boundary, rather than relying only on a later synchronous
			// `signal.aborted` read. The replacement call can arrive in the same task as the abort,
			// while the old operation is still unwinding; marking this entry dead makes that ordering
			// deterministic and leaves the generation fence to protect the successor from stale finally
			// cleanup.
			const onCancel = () => {
				uploadEntry.cancelled = true;
			};
			cancelSignal?.addEventListener("abort", onCancel, { once: true });
			const uploadPromise: Promise<Result> = Promise.resolve()
				.then(() => uploadOperation(attempt))
				.finally(() => {
					cancelSignal?.removeEventListener("abort", onCancel);
					if (inFlightUploadsByUploadId.get(uploadId)?.promise === uploadPromise) {
						inFlightUploadsByUploadId.delete(uploadId);
					}
				});
			uploadEntry.promise = uploadPromise;
			inFlightUploadsByUploadId.set(uploadId, uploadEntry);
			return uploadPromise;
		},
		runResumeWithCancellation: (sessionId, externalSignal, produceOutcome) =>
			resumeCancellationRegistry.run(sessionId, externalSignal, produceOutcome),
		async runPreCompletion(cancelSignal, createError, complete) {
			if (cancelSignal?.aborted) throw createError();
			return complete();
		}
	};
}
